import { cva, type VariantProps } from 'class-variance-authority';

/**
 * The button recipe, in a module with no `'use client'` anywhere in its import
 * graph and its own `@vs-shop/ui/buttonVariants` subpath. A server compound
 * that paints its own CTA element — an `<a href>` a section renders itself —
 * calls this directly; reaching it through `@vs-shop/ui/button` instead would
 * route the call through `Button.tsx`'s client boundary, where the export is a
 * client reference and calling it throws.
 *
 * `Button` paints with this same recipe: the styles have one source, and a
 * server-composed CTA and a `<Button>` render the same classes.
 *
 * Pinned server-safe by `tools/codemods/clientBoundaryScan.unit.test.ts`.
 */
export const buttonVariants = cva(
  [
    'font-heading',
    'group relative flex items-center justify-center gap-2 rounded-lg tracking-widest',
    'border',
    "shrink-0 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    'focus-visible:ring-foreground/30 focus-visible:ring-2 focus-visible:outline-none',
    'aria-invalid:border-destructive',
    'cursor-pointer',
  ],
  {
    variants: {
      variant: {
        primary:
          'border-button-primary bg-button-primary text-button-primary-foreground hover:border-button-primary-hover-border hover:bg-button-primary-hover hover:text-button-primary-hover-foreground',
        secondary:
          'border-foreground/40 bg-button-secondary text-button-secondary-foreground hover:bg-button-secondary-hover hover:text-button-secondary-foreground-hover',
        newsletterInput:
          'border-button-primary bg-button-primary text-button-primary-foreground hover:border-button-primary-hover-border hover:bg-button-primary-hover hover:text-button-primary-hover-foreground',
        ghost: 'border-foreground/40 text-foreground bg-transparent',
        ghostInverted: 'border-foreground-inverted text-foreground-inverted bg-transparent',
        destructive:
          'border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/80',
      },
      size: {
        sm: 'h-12 px-4',
        base: 'h-12 px-10',
        xl: 'h-[60px] px-10',
        '2xl': 'h-16 px-10 text-[22px] font-medium',
        icon: 'h-12 w-12',
        blockXl: 'tab:w-auto tab:px-10 h-[60px] w-full px-4 uppercase',
        block2xl:
          'tab:w-auto tab:gap-4 tab:px-10 tab:text-[22px] h-16 w-full px-4 text-lg font-medium uppercase',
        none: '',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50',
      },
      loading: {
        true: 'cursor-wait opacity-50 hover:opacity-50',
      },
    },
    compoundVariants: [
      {
        variant: 'newsletterInput',
        className: 'h-14 rounded-l-none rounded-r-lg px-6 text-base/5',
      },
    ],
    defaultVariants: {
      variant: 'secondary',
      size: 'base',
    },
  }
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
